/**
 * Converts plan data into GraphViz DOT format for visualization
 *
 * @param {string} planData - The raw plan data as a string
 * @param {string} memData - The memory data as a string (optional)
 * @returns {string} - The DOT language representation of the plan
 */
export const planToDot = (planData, memData) => {
  if (!planData) return "";

  // Parse the memory data if available
  const memoryMap = {};
  if (memData) {
    const memLines = memData.split("\n");
    for (let line of memLines) {
      line = line.trim();
      if (line && line.startsWith("%m")) {
        const match = line.match(/(%m\d+):\s*(.*)/);
        if (match) {
          const memKey = match[1];
          const memValue = match[2];
          memoryMap[memKey] = memValue;
        }
      }
    }
  } else {
    console.log("No memory data provided"); // Debug: Log when no memory data
  }

  // Parse the plan data
  const tasks = [];
  const blocks = [];
  const edges = [];

  // Track all block IDs across all tasks for intra-task jumping
  const allBlocksMap = {};

  // Task mapping (task ID to task object)
  const taskMap = {};

  // Current task being parsed
  let currentTask = null;
  let currentBlock = null;

  // Process each line
  const lines = planData.split("\n");

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i].trim();

    if (!line || line.startsWith("///") || line.startsWith("plan,")) {
      continue; // Skip empty lines, comments, and plan header
    }

    // Task definition
    if (line.startsWith("task,")) {
      const taskMatch = line.match(/id:\s*(\d+|\d+)\s*,.*block:\s*(-?\d+)/);
      if (taskMatch) {
        const taskId = taskMatch[1];
        const startBlockId = taskMatch[2];

        currentTask = {
          id: taskId,
          startBlock: startBlockId,
          description: "",
          blocks: [],
        };

        // Check if the next line contains a description
        if (
          i + 1 < lines.length &&
          lines[i + 1].trim().startsWith("description:")
        ) {
          currentTask.description = lines[i + 1]
            .trim()
            .substring("description:".length)
            .trim();
          i++; // Skip the description line in the next iteration
        }

        taskMap[taskId] = currentTask;
        tasks.push(currentTask);
      }
      continue;
    }

    // Block definition
    if (line.startsWith("#")) {
      const blockMatch = line.match(/#(\d+):(.*)/);
      if (blockMatch && currentTask) {
        const blockId = blockMatch[1];
        const blockDescription = blockMatch[2] ? blockMatch[2].trim() : "";

        currentBlock = {
          id: blockId,
          taskId: currentTask.id,
          description: blockDescription,
          instructions: [],
        };

        currentTask.blocks.push(currentBlock);
        blocks.push(currentBlock);

        // Register this block in the global map for cross-task jumps
        allBlocksMap[blockId] = {
          taskId: currentTask.id,
          blockId: blockId,
        };
      }
      continue;
    }

    // Instruction (if in a block)
    if (currentBlock && line.trim() && !line.startsWith("/")) {
      currentBlock.instructions.push(line.trim());

      // Check for SEVTL (edge definition)
      const sevtlMatch = line.match(/SEVTL\s+(%m\d+)\s+(%m\d+)\s+(\d+)$/);
      if (sevtlMatch) {
        const memKey = sevtlMatch[1];
        const targetTaskId = sevtlMatch[3];

        // Check memory value to determine edge style
        const memValue = memoryMap[memKey];
        const isEdgeDotted = memValue === "1" || memValue === 1;

        edges.push({
          from: currentBlock.id,
          fromTask: currentTask.id,
          to: targetTaskId,
          toBlock: null, // Going to task's start block
          type: "task",
          style: isEdgeDotted ? "dotted" : "solid",
          color: "black",
        });
      }

      // Check for JMP (unconditional jump)
      const jmpMatch = line.match(/JMP\s+(\d+)/);
      if (jmpMatch) {
        const targetBlockId = jmpMatch[1];
        edges.push({
          from: currentBlock.id,
          fromTask: currentTask.id,
          to: targetBlockId,
          toTask: currentTask.id, // Same task for jumps within the task
          type: "block",
          style: "dashed",
          color: "blue",
        });
      }

      // Check for JEQ (conditional jump)
      const jeqMatch = line.match(/JEQ\s+.*\s+(\d+)/);
      if (jeqMatch) {
        const targetBlockId = jeqMatch[1];
        edges.push({
          from: currentBlock.id,
          fromTask: currentTask.id,
          to: targetBlockId,
          toTask: currentTask.id, // Same task for jumps within the task
          type: "block",
          style: "dashed",
          color: "blue",
        });
      }

      // Check for A_CALL (function call with success/error paths)
      const acallMatch = line.match(/A_CALL\s+.*\s+(\d+)\s+(\d+)$/);
      if (acallMatch) {
        const successTaskId = acallMatch[1];
        const errorTaskId = acallMatch[2];

        // Success edge (normal/black)
        edges.push({
          from: currentBlock.id,
          fromTask: currentTask.id,
          to: successTaskId,
          toBlock: null,
          type: "task",
          style: "solid",
          color: "black",
        });

        // Error edge (red)
        edges.push({
          from: currentBlock.id,
          fromTask: currentTask.id,
          to: errorTaskId,
          toBlock: null,
          type: "task",
          style: "solid",
          color: "red",
        });
      }

      // Check for WIDGETIZE_FETCH (similar to A_CALL)
      const widgetMatch = line.match(/WIDGETIZE_FETCH\s+.*\s+(\d+)\s+(\d+)$/);
      if (widgetMatch) {
        const successTaskId = widgetMatch[1];
        const errorTaskId = widgetMatch[2];

        // Success edge (normal/black)
        edges.push({
          from: currentBlock.id,
          fromTask: currentTask.id,
          to: successTaskId,
          toBlock: null,
          type: "task",
          style: "solid",
          color: "black",
        });

        // Error edge (red)
        edges.push({
          from: currentBlock.id,
          fromTask: currentTask.id,
          to: errorTaskId,
          toBlock: null,
          type: "task",
          style: "solid",
          color: "red",
        });
      }
    }
  }

  // Generate DOT representation
  let dot = "digraph Plan {\n";
  dot += "  compound=true;\n";
  dot += "  node [shape=box, style=filled, fillcolor=lightblue];\n";
  dot += '  label="Automation Plan";\n';
  dot += '  labelloc="t";\n\n';

  // Create task subgraphs
  for (const task of tasks) {
    if (task.startBlock === "-1") {
      // Handle tasks with no blocks
      dot += `  subgraph cluster_task_${task.id} {\n`;
      const taskLabel = task.description
        ? `${task.id}: ${task.description}`
        : `${task.id}: unknown`;
      dot += `    label="${taskLabel}";\n`;
      dot += "    style=filled;\n";
      dot += "    color=lightgrey;\n";
      dot += `    dummy_${task.id} [label="No Blocks", style=dashed, fillcolor=white];\n`;
      dot += "  }\n\n";
    } else {
      dot += `  subgraph cluster_task_${task.id} {\n`;
      const taskLabel = task.description
        ? `${task.id}: ${task.description}`
        : `${task.id}: unknown`;
      dot += `    label="${taskLabel}";\n`;
      dot += "    style=filled;\n";
      dot += "    color=lightgrey;\n";

      // Add all blocks
      for (const block of task.blocks) {
        // Only include block ID and description in label, no instruction snippets
        const blockLabel = block.description
          ? `#${block.id}: ${block.description}`
          : `#${block.id}: unknown`;
        dot += `    block_${task.id}_${block.id} [label="${blockLabel}"];\n`;
      }

      dot += "  }\n\n";
    }
  }

  // Remove automatic sequential block connections
  // No longer adding sequential edges - only explicit JMP and JEQ connections are used

  // Add all edge connections between blocks and tasks
  for (const edge of edges) {
    if (edge.type === "task") {
      // Edge to another task
      if (taskMap[edge.to]) {
        const targetTask = taskMap[edge.to];

        // Handle tasks with no blocks
        if (targetTask.startBlock === "-1") {
          dot += `  block_${edge.fromTask}_${edge.from} -> dummy_${edge.to} [color=${edge.color}, style=${edge.style}, penwidth=1.5, lhead=cluster_task_${edge.to}];\n`;
        }
        // Connect to the target task's start block if it exists
        else {
          const targetStartBlock = targetTask.blocks.find(
            b => b.id === targetTask.startBlock,
          );
          if (targetStartBlock) {
            dot += `  block_${edge.fromTask}_${edge.from} -> block_${edge.to}_${targetStartBlock.id} [color=${edge.color}, style=${edge.style}, penwidth=1.5, lhead=cluster_task_${edge.to}];\n`;
          } else if (targetTask.blocks.length > 0) {
            // If start block not found but task has blocks, connect to the first block
            dot += `  block_${edge.fromTask}_${edge.from} -> block_${edge.to}_${targetTask.blocks[0].id} [color=${edge.color}, style=${edge.style}, penwidth=1.5, lhead=cluster_task_${edge.to}];\n`;
          }
        }
      }
    } else if (edge.type === "block") {
      // Edge to another block within the same task
      dot += `  block_${edge.fromTask}_${edge.from} -> block_${edge.toTask}_${edge.to} [style=${edge.style}, color=${edge.color}];\n`;
    }
  }

  dot += "}\n";
  return dot;
};

/**
 * Create a short summary of block instructions for the node label
 *
 * @param {string[]} instructions - Array of instructions in the block
 * @returns {string} - Short summary for display in the node
 */
function summarizeBlock(instructions) {
  if (!instructions || instructions.length === 0) {
    return "Empty Block";
  }

  // Limit to first 3 instructions for display
  const displayInstructions = instructions.slice(0, 3);
  let summary = displayInstructions
    .map(instr => {
      // Escape quotes in instructions for DOT format
      let escaped = instr.replace(/"/g, '\\"');

      // Truncate long instructions
      return escaped.length > 40 ? escaped.substring(0, 37) + "..." : escaped;
    })
    .join("\\n");

  // Add ellipsis if there are more instructions
  if (instructions.length > 3) {
    summary += "\\n...";
  }

  return summary;
}

/**
 * Helper function to determine if a string is a valid GraphViz DOT representation
 *
 * @param {string} dot - The DOT string to validate
 * @returns {boolean} - True if the string is a valid DOT representation
 */
export const isValidDot = dot => {
  return dot.trim().startsWith("digraph") && dot.trim().endsWith("}");
};

export default { planToDot, isValidDot };
